/**
 * FedRAMP KSI readiness surface — V98-5-8 (uses the v9.6 persistent
 * validation: `ksi_validation_runs`, ADR-0045 §3–4; reconciles with the
 * R3-U14 hero arithmetic).
 *
 *   counts      met + partial + gaps = total, where gaps = total − met − partial
 *               (a KSI with no mapped control is a gap: nothing proves it) — the
 *               SAME split as the FedRAMP Prepare hero (web `readinessSplit`).
 *   automated   "KSIs with automated validation: N of M" — a KSI is automated
 *               when a crediting control is evidenced by a connected source
 *               (machine mode), or its latest validation run was a machine run.
 *   validation  the latest run per KSI (status, when, next due), how many KSIs
 *               ran in the last cycle, which are overdue, and the cadence rules
 *               (rule + version, or verification pending — then validation is
 *               blocked, never silently skipped).
 *
 * V103-6-5 / ADR-0068 §4: which engine produces these rows is the
 * `fedramp-readiness` rule set. Before the switch (legacy `ksi`) the counts
 * come from graph readiness as above; after it, from the KSI objective view
 * (objective engine: KSI status = AND over its objectives, machine + human by
 * worst-of; no SEC-* crediting). The row shape is kept for the SPA; the
 * objective status, FRC-CSX-VVK automated methods, lapse and overdue flags
 * ride along (`objective_view`).
 *
 * Read-only; workspace-scoped.
 */
import { getProgramManifest, machineValidationRule, programHas } from '../programs/manifests.js';
import { SECURITY_BASELINE_CONTROLS } from '../data/security-baseline.js';
import { addTimeframe, getProfile, lookupTimeframe, timeframeProvenance } from './certification-service.js';
import { computeFrameworkReadiness } from './requirement-readiness-service.js';
import { resolveReadinessEngine } from './program-rule-set-service.js';
import { buildFedrampKsiObjectiveView, buildKsiClassAutomation } from './fedramp-ksi-service.js';
const MODE = new Map(SECURITY_BASELINE_CONTROLS.map((c) => [c.id, c.mode]));
/** R3-U14: the hero split — gaps are everything not met or partial (unmapped included). */
export function ksiSplit(c) {
    const gaps = Math.max(0, c.total - c.met - c.partial);
    return { met: c.met, partial: c.partial, gaps, total: c.met + c.partial + gaps };
}
export async function buildKsiReadiness(db, workspaceId, profileId, opts = {}) {
    const now = opts.now ?? new Date();
    const nowIso = now.toISOString();
    const profile = await getProfile(db, workspaceId, profileId);
    if (!profile || !programHas(profile.program, (m) => !!m.features.persistentValidation))
        throw new Error('Not a FedRAMP program.');
    const content = opts.contentDb ?? db;
    if ((await resolveReadinessEngine(content, profile.program)).engine === 'objective') {
        return buildKsiReadinessFromObjectives(db, workspaceId, profileId, { now, contentDb: content });
    }
    const readiness = await computeFrameworkReadiness(db, workspaceId, getProgramManifest(profile.program).frameworkId, { certificationClass: profile.certification_class, now, contentDb: content, profileId });
    const path = profile.path ?? '20x';
    const machineRule = machineValidationRule(getProgramManifest(profile.program), path);
    const machine = await lookupTimeframe(content, machineRule, { path, certificationClass: profile.certification_class, pain: null, variant: null });
    const nonMachine = await lookupTimeframe(content, 'VDR-TFR-NMV', { path, certificationClass: profile.certification_class, pain: null, variant: null });
    const runs = await db.query(`SELECT requirement_id, status, validation_type, completed_at, due_at, reason FROM ksi_validation_runs
      WHERE workspace_id = ? AND profile_id = ? ORDER BY completed_at DESC, run_id ASC`, [workspaceId, profileId]);
    const latest = new Map();
    const count = new Map();
    for (const r of runs.results) {
        count.set(r.requirement_id, (count.get(r.requirement_id) ?? 0) + 1);
        if (!latest.has(r.requirement_id))
            latest.set(r.requirement_id, { status: r.status, validation_type: r.validation_type, completed_at: r.completed_at, due_at: r.due_at, reason: r.reason });
    }
    const every = (tf) => (tf ? `every ${tf.timeframe_num} ${tf.timeframe_type === 'bizdays' ? 'business days' : tf.timeframe_type}` : null);
    const nextDue = (run, isMachine) => {
        const tf = isMachine ? machine : nonMachine;
        // The one calculator (business days skip weekends + US federal holidays, V98-0-4).
        return run && tf ? addTimeframe(new Date(run.completed_at), tf.timeframe_num, tf.timeframe_type).toISOString() : null;
    };
    const rows = readiness.requirements.map((q) => {
        const run = latest.get(q.requirement_id) ?? null;
        const machineControl = q.crediting_controls.some((c) => (c.evidence_mode ?? MODE.get(c.control_id)) === 'machine');
        const automated = machineControl || run?.validation_type === 'machine';
        const attested = !automated && q.crediting_controls.some((c) => (c.evidence_mode ?? MODE.get(c.control_id)) === 'attested');
        const next = nextDue(run, automated);
        return {
            requirement_id: q.requirement_id, ref: q.ref, title: q.title, group_ref: q.group_ref, status: q.status, reason: q.reason,
            automated, proven_by: automated ? 'automated' : attested ? 'attested' : 'none',
            latest_run: run, runs: count.get(q.requirement_id) ?? 0,
            validation_overdue: !!next && next < nowIso, next_due_at: next,
        };
    });
    const c = readiness.counts;
    const split = ksiSplit({ met: c.met ?? 0, partial: c.partial ?? 0, total: c.total });
    const lastRun = runs.results[0]?.completed_at ?? null;
    const cycleStart = lastRun ? new Date(Date.parse(lastRun) - 86_400_000).toISOString() : null;
    return {
        engine: 'ksi',
        profile_id: profileId, certification_class: profile.certification_class, generated_at: nowIso,
        counts: split, readiness_pct: readiness.readiness_pct,
        automated: { automated: rows.filter((r) => r.automated).length, total: rows.length },
        validation: {
            last_run_at: lastRun,
            ran: cycleStart ? rows.filter((r) => r.latest_run && r.latest_run.completed_at >= cycleStart).length : 0,
            of: rows.length,
            overdue: rows.filter((r) => r.validation_overdue).length,
            blocked_reason: !machine || !nonMachine ? 'FedRAMP validation cadences are not approved yet — persistent validation is paused until the FedRAMP rules batch is approved.' : null,
            cadence: { machine: timeframeProvenance(machineRule, machine), non_machine: timeframeProvenance('VDR-TFR-NMV', nonMachine), machine_every: every(machine), non_machine_every: every(nonMachine) },
        },
        rows,
        class_automation: await buildKsiClassAutomation(db, workspaceId, { contentDb: content }),
    };
}
/** The KSI readiness surface from the objective engine (ADR-0068 §4); same row shape, objective status alongside. */
async function buildKsiReadinessFromObjectives(db, workspaceId, profileId, opts) {
    const nowIso = opts.now.toISOString();
    const view = await buildFedrampKsiObjectiveView(db, workspaceId, profileId, { now: opts.now, contentDb: opts.contentDb });
    const profile = (await getProfile(db, workspaceId, profileId));
    const path = profile.path ?? '20x';
    const machineRule = machineValidationRule(getProgramManifest(profile.program), path);
    const machine = await lookupTimeframe(opts.contentDb, machineRule, { path, certificationClass: profile.certification_class, pain: null, variant: null });
    const nonMachine = await lookupTimeframe(opts.contentDb, 'VDR-TFR-NMV', { path, certificationClass: profile.certification_class, pain: null, variant: null });
    const runs = await db.query(`SELECT requirement_id, status, validation_type, completed_at, due_at, reason FROM ksi_validation_runs
      WHERE workspace_id = ? AND profile_id = ? ORDER BY completed_at DESC, run_id ASC`, [workspaceId, profileId]);
    const latest = new Map();
    const count = new Map();
    for (const r of runs.results) {
        count.set(r.requirement_id, (count.get(r.requirement_id) ?? 0) + 1);
        if (!latest.has(r.requirement_id))
            latest.set(r.requirement_id, { status: r.status, validation_type: r.validation_type, completed_at: r.completed_at, due_at: r.due_at, reason: r.reason });
    }
    const every = (tf) => (tf ? `every ${tf.timeframe_num} ${tf.timeframe_type === 'bizdays' ? 'business days' : tf.timeframe_type}` : null);
    const rows = view.rows.map((r) => {
        const run = latest.get(r.requirement_id) ?? null;
        const automated = r.objectives.some((o) => o.machine_validated);
        const attested = r.objectives.some((o) => o.sources?.human);
        const tf = automated ? machine : nonMachine;
        const next = run && tf ? addTimeframe(new Date(run.completed_at), tf.timeframe_num, tf.timeframe_type).toISOString() : null;
        const mapped = r.objectives.some((o) => o.test_keys.length);
        const status = r.status === 'met' ? 'met' : r.status === 'undetermined' && !mapped ? 'unmapped' : 'gap';
        return {
            requirement_id: r.requirement_id, ref: r.ref, title: r.title, group_ref: r.group_ref, status,
            reason: r.status === 'met' ? 'Every KSI objective is met.' : `KSI objectives ${r.status.replace('_', ' ')}.`,
            automated, proven_by: automated ? 'automated' : attested ? 'attested' : 'none',
            latest_run: run, runs: count.get(r.requirement_id) ?? 0, validation_overdue: !!next && next < nowIso, next_due_at: next,
            objective_status: r.status, automated_methods: r.automated_methods, lapsed: r.lapsed,
        };
    });
    const met = rows.filter((r) => r.status === 'met').length;
    const lastRun = runs.results[0]?.completed_at ?? null;
    const cycleStart = lastRun ? new Date(Date.parse(lastRun) - 86_400_000).toISOString() : null;
    return {
        engine: 'objective',
        profile_id: profileId, certification_class: profile.certification_class, generated_at: nowIso,
        counts: ksiSplit({ met, partial: 0, total: rows.length }),
        readiness_pct: rows.length ? Math.round((met / rows.length) * 1000) / 10 : 0,
        automated: { automated: rows.filter((r) => r.automated).length, total: rows.length },
        validation: {
            last_run_at: lastRun,
            ran: cycleStart ? rows.filter((r) => r.latest_run && r.latest_run.completed_at >= cycleStart).length : 0,
            of: rows.length,
            overdue: rows.filter((r) => r.validation_overdue).length,
            blocked_reason: !machine || !nonMachine
                ? 'FedRAMP validation cadences are not approved yet — persistent validation is paused until the FedRAMP rules batch is approved.'
                : view.blocked_reason === 'no_period' ? 'No period yet: start a period so persistent validation can record results.' : null,
            cadence: { machine: timeframeProvenance(machineRule, machine), non_machine: timeframeProvenance('VDR-TFR-NMV', nonMachine), machine_every: every(machine), non_machine_every: every(nonMachine) },
        },
        rows,
        class_automation: view.class_automation,
        objective_view: view,
    };
}
//# sourceMappingURL=ksi-readiness-service.js.map